import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Luk dropdown og mobilmenu ved sideskift
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Luk dropdown når der klikkes udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const ydelser = [
    { name: 'Nyt tag', path: '/ydelser/nyt-tag' },
    { name: 'Terrasser', path: '/ydelser/terrasser' },
    { name: 'Tilbygninger & renovering', path: '/ydelser/tilbygninger-renovering' },
    { name: 'Døre & vinduer', path: '/ydelser/doere-vinduer' },
  ];

  const isActive = (path) => location.pathname === path;
  const isYdelseActive = ydelser.some(y => location.pathname === y.path);

  return (
    <header className="w-full bg-brand-light border-b border-gray-100 sticky top-0 z-50 shadow-xs">
      {/* Top Bar - Kontaktoplysninger */}
      <div className="bg-brand-dark text-white py-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 text-xs sm:text-sm">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <a href="tel:+4528455012" className="flex items-center gap-2 hover:text-brand-wood transition-colors duration-200 whitespace-nowrap">
              <i className="fa-solid fa-phone text-brand-wood" />
              <span>28 45 50 12</span>
            </a>
            <a href="mailto:Mikkeljuler@gmail.com" className="flex items-center gap-2 hover:text-brand-wood transition-colors duration-200 whitespace-nowrap">
              <i className="fa-solid fa-envelope text-brand-wood" />
              <span>Mikkeljuler@gmail.com</span>
            </a>
            <a 
              href="https://maps.google.com/?q=Koldingvej+49B,+6040+Egtved" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-2 hover:text-brand-wood transition-colors duration-200 whitespace-nowrap"
            >
              <i className="fa-solid fa-location-dot text-brand-wood" />
              <span>Koldingvej 49B, 6040 Egtved</span>
            </a>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-brand-wood font-medium">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Lokalt engagement & godt håndværk</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=90,format=auto/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/logo-1.png" 
              alt="Tømrermester Mikkel Juler ApS Logo" 
              className="h-18 sm:h-22 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
            />
          </Link>

          {/* Desktop Menu (Skærmbredder >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            <Link 
              to="/" 
              className={`font-medium text-sm transition-colors duration-200 hover:text-brand-wood ${
                isActive('/') ? 'text-brand-wood border-b-2 border-brand-wood pb-1' : 'text-brand-dark'
              }`}
            >
              Forside
            </Link>

            {/* Dropdown for Ydelser */}
            <div 
              className="relative" 
              ref={dropdownRef}
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button 
                className={`flex items-center gap-1.5 font-medium text-sm transition-colors duration-200 hover:text-brand-wood cursor-pointer ${
                  isYdelseActive ? 'text-brand-wood border-b-2 border-brand-wood pb-1' : 'text-brand-dark'
                }`}
              >
                <span>Ydelser</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-64 z-50">
                  <div className="rounded-xl bg-white border border-gray-100 shadow-xl py-2">
                    {ydelser.map((ydelse) => (
                      <Link
                        key={ydelse.path}
                        to={ydelse.path}
                        className={`block px-4 py-2.5 text-sm transition-colors duration-150 hover:bg-brand-wood-light hover:text-brand-wood-dark ${
                          isActive(ydelse.path) ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark'
                        }`}
                      >
                        {ydelse.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/projekter" 
              className={`font-medium text-sm transition-colors duration-200 hover:text-brand-wood ${
                isActive('/projekter') ? 'text-brand-wood border-b-2 border-brand-wood pb-1' : 'text-brand-dark'
              }`}
            >
              Projekter
            </Link>

            <Link 
              to="/om-mikkel" 
              className={`font-medium text-sm transition-colors duration-200 hover:text-brand-wood ${
                isActive('/om-mikkel') ? 'text-brand-wood border-b-2 border-brand-wood pb-1' : 'text-brand-dark'
              }`}
            >
              Om Mikkel
            </Link>

            <Link 
              to="/kontakt" 
              className={`font-medium text-sm transition-colors duration-200 hover:text-brand-wood ${
                isActive('/kontakt') ? 'text-brand-wood border-b-2 border-brand-wood pb-1' : 'text-brand-dark'
              }`}
            >
              Kontakt
            </Link>

            {/* CTA Button */}
            <Link 
              to="/kontakt" 
              className="bg-brand-green hover:bg-brand-green/90 text-white font-semibold text-sm px-5 py-2.5 rounded-xl shadow-xs hover:shadow-md transition-all duration-200"
            >
              Få et tilbud
            </Link>
          </nav>

          {/* Burger Menu Button (Skærmbredder < 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-xl text-brand-dark hover:text-brand-wood hover:bg-gray-50 focus:outline-none cursor-pointer"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Åbn hovedmenu</span>
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Skærmbredder < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-white border-t border-gray-100 max-h-[calc(100vh-10rem)] overflow-y-auto" id="mobile-menu">
          <div className="px-4 pt-2 pb-6 space-y-2">
            <Link
              to="/"
              className={`block px-3 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/') ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark hover:bg-gray-50'
              }`}
            >
              Forside
            </Link>

            {/* Ydelser Section in Mobile Menu */}
            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Ydelser
              </div>
              {ydelser.map((ydelse) => (
                <Link
                  key={ydelse.path}
                  to={ydelse.path}
                  className={`block pl-6 pr-3 py-2 rounded-xl text-base font-medium transition-colors ${
                    isActive(ydelse.path) ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark hover:bg-gray-50'
                  }`}
                >
                  {ydelse.name}
                </Link>
              ))}
            </div>

            <Link
              to="/projekter"
              className={`block px-3 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/projekter') ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark hover:bg-gray-50'
              }`}
            >
              Projekter
            </Link>

            <Link
              to="/om-mikkel"
              className={`block px-3 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/om-mikkel') ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark hover:bg-gray-50'
              }`}
            >
              Om Mikkel
            </Link>

            <Link
              to="/kontakt"
              className={`block px-3 py-2.5 rounded-xl text-base font-medium transition-colors ${
                isActive('/kontakt') ? 'bg-brand-wood-light text-brand-wood-dark font-semibold' : 'text-brand-dark hover:bg-gray-50'
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-4 px-3">
              <Link
                to="/kontakt"
                className="block w-full text-center bg-brand-green hover:bg-brand-green/90 text-white font-semibold px-4 py-3 rounded-xl shadow-xs"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
